import React, { useState, useContext, useEffect } from 'react';
import { useMenus } from './MenusContext';
import { useLayout } from '../../Layout/LayoutContext';
import { NavigationContext } from '~/context/navigation';
import { InformacaoTelasAcessadasContext } from '~/context/InformacaoTelasAcessadas';
import useAuth from '~/hooks/useAuth';
import {
  RenderTelasList,
  processMoreAccessedTelas,
  processRecentTelas,
  processFavoriteTelas,
} from './TelaList';
import { RightBar, MenuItem, MenuLink } from '../styles';
import { FaEllipsisV, FaStar, FaRegStar } from 'react-icons/fa';
import { StorageService } from '~/utils/classes/StorageService';

export const MenuContent: React.FC = () => {
  const {
    viewMode,
    moreAccessed,
    recentes,
    trilhas,
    handleFavorite,
    handleAddAccessed,
    setViewMode,
  } = useMenus();
  const { telas, setCollapsed, setSelectedModulo } = useLayout();
  const { addScreen } = useContext(NavigationContext);
  const { addScreenInfo } = useContext(InformacaoTelasAcessadasContext);
  const user = useAuth();
  const [expandedTrilhas, setExpandedTrilhas] = useState<number[]>([]);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const store = new StorageService();

  const toggleTrilha = (codTrilha: number) => {
    setExpandedTrilhas((prev) =>
      prev.includes(codTrilha)
        ? prev.filter((id) => id !== codTrilha)
        : [...prev, codTrilha],
    );
  };

  const handleTelaClick = (tela: any) => {
    // Verificação de privilégios igual ao NavbarOld
    if (tela.cod_tela === 280 && !user.user?.flg_lib_financeiro) {
      return; // Não abrir tela se não tem privilégio
    }

    if (tela.content === null || tela.content === undefined) {
      return; // Não abrir tela se não tem content
    }

    // Criar screen no mesmo formato do NavbarOld
    const screen = {
      content: tela.content,
      label: tela.label_menu,
      lib_icon: tela.lib_icon,
      des_icon: tela.des_icon,
      cod_tela: tela.cod_tela,
      url: tela.url,
      linkMaterial: tela.link_material,
      flg_abre_pesquisa: tela.flg_abre_pesquisa,
    };

    handleAddAccessed(tela.cod_tela);
    addScreen(screen);
    addScreenInfo(screen);

    setCollapsed(true);
    store.save('session', 'collapsed', true);
    setViewMode('modules');
    setSelectedModulo(2);
  };

  const toggleDropdown = (e: React.MouseEvent, telaId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenDropdown(openDropdown === telaId ? null : telaId);
  };

  const handleFavoriteClick = async (e: React.MouseEvent, tela: any) => {
    e.preventDefault();
    e.stopPropagation();
    const favoriteStatus = tela.flg_favorita;
    await handleFavorite(tela.cod_tela, favoriteStatus);

    // Atualizar o estado local da tela na trilha
    tela.flg_favorita = !favoriteStatus;

    setOpenDropdown(null);
  };

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const renderContent = () => {
    switch (viewMode) {
      case 'most-accessed':
        return (
          <RenderTelasList
            telas={processMoreAccessedTelas(moreAccessed, telas)}
          />
        );

      case 'recent':
        return <RenderTelasList telas={processRecentTelas(recentes, telas)} />;

      case 'favorites':
        return <RenderTelasList telas={processFavoriteTelas(telas)} />;

      case 'trails': {
        if (trilhas.length === 0) {
          return (
            <MenuItem>
              <div
                style={{
                  padding: '20px',
                  color: '#666',
                  textAlign: 'center',
                }}
              >
                Nenhuma trilha disponível. Consulte seu administrador do
                sistema.
              </div>
            </MenuItem>
          );
        }

        return (
          <>
            {trilhas.map((trilha) => (
              <React.Fragment key={trilha.cod_trilha}>
                <MenuItem>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: '10px 16px 10px 24px',
                      cursor: 'pointer',
                      gap: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: '#fff',
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      transition: 'background-color 0.2s',
                    }}
                    onClick={() => toggleTrilha(trilha.cod_trilha)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#6e40bd';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <span style={{ fontSize: '12px' }}>
                      {expandedTrilhas.includes(trilha.cod_trilha) ? '▾' : '▸'}
                    </span>
                    {trilha.Icon && <trilha.Icon size={16} />}
                    {trilha.des_trilha}
                  </div>
                </MenuItem>
                {expandedTrilhas.includes(trilha.cod_trilha) &&
                  trilha.telas &&
                  trilha.telas.length > 0 &&
                  trilha.telas.map((tela: any) => (
                    <MenuItem key={tela.cod_tela}>
                      <MenuLink
                        to={tela.url || '/app'}
                        style={{ paddingLeft: '48px' }}
                        onClick={(e) => {
                          if (
                            (e.target as HTMLElement).closest('.actions-button')
                          ) {
                            e.preventDefault();
                            return;
                          }
                          handleTelaClick(tela);
                        }}
                      >
                        {tela.Icon && <tela.Icon size={18} />}
                        {tela.des_tela}

                        {tela.flg_favorita && (
                          <span
                            className="favorite-indicator"
                            title="Tela favoritada"
                          />
                        )}

                        <button
                          type="button"
                          className="actions-button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleDropdown(e, tela.cod_tela.toString());
                          }}
                          title="Ações"
                        >
                          <FaEllipsisV size={12} />
                        </button>

                        {openDropdown === tela.cod_tela.toString() && (
                          <div className="dropdown">
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={(e) => handleFavoriteClick(e, tela)}
                            >
                              {tela.flg_favorita ? (
                                <>
                                  <FaStar size={14} />
                                  Remover dos favoritos
                                </>
                              ) : (
                                <>
                                  <FaRegStar size={14} />
                                  Adicionar aos favoritos
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </MenuLink>
                    </MenuItem>
                  ))}
              </React.Fragment>
            ))}
          </>
        );
      }

      default:
        return null;
    }
  };

  if (viewMode === 'modules') {
    return null;
  }

  return <RightBar>{renderContent()}</RightBar>;
};
